'use client'

import Link from 'next/link'
import { cn } from '@/shared/lib/utils'
import { buildRegionUrl } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import { formatDateRange, formatGroupDateRange } from '@/features/stm/lib/date-utils'
import type { WeekWithStats, TripType, Group } from '@/features/stm/lib/types'
import { Users, AlertTriangle, Ban, CalendarDays, Shield } from 'lucide-react'
import { format } from 'date-fns'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { SEARCH_PARAMS_WEEK_START_KEY } from '@/features/volunteers/constants/volunteer-tab-links'

interface WeekCardProps {
  week: WeekWithStats
  onClick: () => void
  isSelected?: boolean
  /** ISO yyyy-MM-dd for the first day of the calendar month being viewed — restores month when returning from group detail */
  calendarMonthAnchor?: string
  /** Base Leaders and Admins onsite this week (from AR Team) */
  arTeamNames?: string[]
  /**
   * `muted` — neutral grey card (e.g. non-current rows in “Weeks in month”); capacity fill bar still uses green/amber/red by fill rate.
   * `default` — full status-tinted card (capacity colors on border/background).
   */
  calendarRowTone?: 'default' | 'muted'
}

function getStatusColor(week: WeekWithStats) {
  if (week.hasHardBlock) return 'blocked'
  if (week.fillRate > 100) return 'over'
  if (week.fillRate >= 90) return 'full'
  if (week.fillRate >= 70) return 'warning'
  return 'available'
}

const statusConfig: Record<
  string,
  { border: string; bg: string; hover: string; fillBar: string; text: string; badge: string }
> = {
  available: {
    border: 'border-emerald-300/60 dark:border-emerald-600/40',
    bg: 'bg-gradient-to-br from-emerald-50/80 to-green-50/50 dark:from-emerald-950/30 dark:to-green-950/20',
    hover: 'hover:shadow-emerald-200/50 dark:hover:shadow-emerald-900/30 hover:border-emerald-400/70',
    fillBar: 'bg-gradient-to-r from-emerald-400 to-green-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  warning: {
    border: 'border-amber-300/60 dark:border-amber-600/40',
    bg: 'bg-gradient-to-br from-amber-50/80 to-yellow-50/50 dark:from-amber-950/30 dark:to-yellow-950/20',
    hover: 'hover:shadow-amber-200/50 dark:hover:shadow-amber-900/30 hover:border-amber-400/70',
    fillBar: 'bg-gradient-to-r from-amber-400 to-yellow-500',
    text: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  },
  full: {
    border: 'border-rose-300/60 dark:border-rose-600/40',
    bg: 'bg-gradient-to-br from-rose-50/80 to-red-50/50 dark:from-rose-950/30 dark:to-red-950/20',
    hover: 'hover:shadow-rose-200/50 dark:hover:shadow-rose-900/30 hover:border-rose-400/70',
    fillBar: 'bg-gradient-to-r from-rose-400 to-red-500',
    text: 'text-rose-600 dark:text-rose-400',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
  },
  over: {
    border: 'border-rose-400/70 dark:border-rose-500/50',
    bg: 'bg-gradient-to-br from-rose-100/90 to-red-100/70 dark:from-rose-900/40 dark:to-red-950/30',
    hover: 'hover:shadow-rose-300/50 dark:hover:shadow-rose-800/30 hover:border-rose-500/80',
    fillBar: 'bg-gradient-to-r from-rose-500 to-red-600',
    text: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-200 text-rose-800 dark:bg-rose-800/60 dark:text-rose-200',
  },
  blocked: {
    border: 'border-slate-300/60 dark:border-slate-600/40',
    bg: 'bg-gradient-to-br from-slate-100/80 to-gray-100/50 dark:from-slate-900/50 dark:to-gray-900/30',
    hover: 'hover:shadow-slate-200/50 dark:hover:shadow-slate-800/30 hover:border-slate-400/70',
    fillBar: 'bg-gradient-to-r from-slate-400 to-gray-500',
    text: 'text-slate-600 dark:text-slate-400',
    badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  },
}

function ArTeamCard({ names, tone = 'default' }: { names: string[]; tone?: 'default' | 'muted' }) {
  if (tone === 'muted') {
    return (
      <div className="w-full min-w-0 rounded-lg border border-primary/30 bg-primary/10 shadow-sm ring-1 ring-primary/25 dark:bg-primary/15 dark:ring-primary/30 p-2 sm:p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-bold text-primary uppercase tracking-wide">
              Base Leaders - {names.length} On Base
            </span>
          </div>
        </div>
        <p className="text-xs sm:text-sm text-foreground font-medium">{names.join(', ')}</p>
      </div>
    )
  }
  return (
    <div className="w-full min-w-0 rounded-lg border-2 border-teal-600/70 bg-transparent shadow-sm border-l-4 border-l-teal-600 p-2 sm:p-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-700 dark:text-teal-300 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-bold text-teal-800 dark:text-teal-200 uppercase tracking-wide">
            Base Leaders - {names.length} On Base
          </span>
        </div>
      </div>
      <p className="text-xs sm:text-sm text-teal-900 dark:text-teal-100 font-medium">
        {names.join(', ')}
      </p>
    </div>
  )
}

const DAY_INITIALS: Record<number, string> = {
  0: 'S', 1: 'M', 2: 'T', 3: 'W', 4: 'Th', 5: 'F', 6: 'Sa',
}

function formatSingleGroupDate(dateStr: string): string {
  const d = new Date(dateStr)
  const day = d.getDay()
  return `${DAY_INITIALS[day] ?? ''}, ${format(d, 'M/d')}`
}

const tripTypeColors: Record<TripType, string> = {
  'Vision Trip': 'bg-blue-500',
  'Skilled Labor': 'bg-amber-500',
  'Volunteer': 'bg-emerald-500',
  'Youth Trip': 'bg-violet-500',
  'Adult Trip': 'bg-indigo-500',
  'Family Trip': 'bg-pink-500',
  'Semesters': 'bg-cyan-500',
  'Gap': 'bg-teal-500',
  'WR': 'bg-orange-500',
  'Leadership': 'bg-slate-500',
  'Partner': 'bg-violet-500',
  'Other': 'bg-gray-400',
}

function GroupCard({
  group,
  embeddedInMutedWeek,
  calendarMonthAnchor,
}: {
  group: Group
  embeddedInMutedWeek?: boolean
  calendarMonthAnchor?: string
}) {
  const { regionSlug } = useRegion()
  const groupQuery: Record<string, string> = { from: 'calendar' }
  if (calendarMonthAnchor && /^\d{4}-\d{2}-\d{2}$/.test(calendarMonthAnchor)) {
    groupQuery[SEARCH_PARAMS_WEEK_START_KEY] = calendarMonthAnchor
  }
  const groupHref = regionSlug
    ? buildRegionUrl(regionSlug, `/volunteers/groups/${group.id}`, groupQuery)
    : `/volunteers/groups/${group.id}?${new URLSearchParams(groupQuery).toString()}`
  const rawLeader = group.leader_name ?? group.contact_name ?? null
  const leaderDisplay = rawLeader?.trim() && !/^(n\/?a)$/i.test(rawLeader.trim()) ? rawLeader.trim() : null
  const registeredParticipants = group.registered_participants ?? 0

  const statusStyles: Record<string, string> = {
    Confirmed: 'border-l-teal-500 bg-teal-50/50 dark:bg-teal-950/20',
    Tentative: 'border-l-amber-500 border-dashed bg-amber-50/50 dark:bg-amber-950/20',
    Cancelled: 'border-l-slate-400 opacity-50 bg-slate-50/50 dark:bg-slate-950/20',
  }

  const cardContent = (
    <Link
      href={groupHref}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'min-w-0 sm:min-w-[200px] max-w-[300px] flex-shrink-0 p-2 sm:p-3 rounded-lg border border-border/60 shadow-sm border-l-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 block text-left no-underline',
        embeddedInMutedWeek
          ? 'bg-muted/60 hover:bg-muted/70 dark:bg-muted/35 dark:hover:bg-muted/45'
          : 'bg-white/90 dark:bg-slate-800/90 hover:bg-muted/50',
        statusStyles[group.status],
        group.status === 'Cancelled' && 'line-through'
      )}
    >
      <div className="flex items-start justify-between gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 flex-wrap">
        <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
          {group.project_id && (
            <span className="text-[10px] sm:text-xs font-bold text-primary bg-primary/10 px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0">
              {group.project_id}
            </span>
          )}
          <span className="font-semibold text-xs sm:text-sm text-foreground leading-tight truncate min-w-0 text-primary underline-offset-2 hover:underline">
            {group.group_name}
          </span>
        </div>
        <span
          className={cn(
            'text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 rounded text-white flex-shrink-0',
            group.trip_type ? tripTypeColors[group.trip_type] : 'bg-zinc-500'
          )}
        >
          {group.trip_type ?? 'Not set'}
        </span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs mb-1 flex-wrap">
        <div className="flex items-center gap-1 text-muted-foreground">
          <Users className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
          <span className="font-medium text-foreground">
            E {group.participants}/R {registeredParticipants}
          </span>
          <span className="hidden sm:inline">participants</span>
          <span className="sm:hidden">p</span>
        </div>
      </div>
      <div className="text-[10px] sm:text-xs mb-1.5 sm:mb-2 truncate min-w-0">
        {leaderDisplay ? (
          <span className="text-muted-foreground">
            ML:{' '}
            <span className="truncate text-primary underline-offset-2 hover:underline">
              {leaderDisplay}
            </span>
          </span>
        ) : (
          <span className="text-red-600 dark:text-red-400 font-medium">ML: ML Needed</span>
        )}
      </div>
      <div className="pt-1.5 sm:pt-2 border-t border-border/40 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] sm:text-xs">
        {(group.start_date || group.end_date) && (
          <span className="text-muted-foreground">
            {group.start_date && group.end_date
              ? formatGroupDateRange(group.start_date, group.end_date)
              : group.start_date
                ? formatSingleGroupDate(group.start_date)
                : group.end_date
                  ? formatSingleGroupDate(group.end_date)
                  : null}
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={cn(
              'font-semibold uppercase tracking-wide',
              group.status === 'Confirmed' && 'text-emerald-600 dark:text-emerald-400',
              group.status === 'Tentative' && 'text-amber-600 dark:text-amber-400',
              group.status === 'Cancelled' && 'text-slate-500'
            )}
          >
            {group.status === 'Confirmed' && '● '}
            {group.status === 'Tentative' && '○ '}
            {group.status}
          </span>
          {(group.retention_group === 'New' || group.retention_group === 'Return' || group.retention_group === 'Retention') && (
            <span className="text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-700 dark:bg-gray-600/40 dark:text-gray-300 border border-gray-400/50 dark:border-gray-500 flex-shrink-0">
              {group.retention_group === 'Retention' ? 'Return' : group.retention_group}
            </span>
          )}
        </div>
      </div>
    </Link>
  )

  if (group.notes?.trim()) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {cardContent}
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-[280px] sm:max-w-sm bg-white dark:bg-slate-800 text-foreground border border-border shadow-md py-2.5 px-3 text-left whitespace-pre-wrap"
          >
            <p className="font-semibold text-primary mb-1">Group Notes</p>
            <p className="text-xs leading-relaxed">{group.notes}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return cardContent
}

export function WeekCard({
  week,
  onClick,
  isSelected,
  arTeamNames = [],
  calendarRowTone = 'default',
  calendarMonthAnchor,
}: WeekCardProps) {
  const status = getStatusColor(week)
  const config = statusConfig[status]
  const dateLabel = formatDateRange(week.week_start, week.week_end)
  const isMutedRow = calendarRowTone === 'muted'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(
        'group relative w-full rounded-xl border-2 transition-all duration-300 text-left overflow-hidden flex flex-col cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        isMutedRow &&
          'border-border bg-muted/50 hover:bg-muted/60 dark:bg-muted/35 dark:hover:bg-muted/45 shadow-sm hover:shadow-md',
        !isMutedRow && config.border,
        !isMutedRow && config.bg,
        !isMutedRow && config.hover,
        !isMutedRow && 'hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0',
        isSelected && 'ring-2 ring-primary ring-offset-2 shadow-lg -translate-y-0.5'
      )}
    >
      <div
        className={cn(
          'p-3 pb-3 sm:pb-2.5 flex-shrink-0 border-b border-border/30',
          isMutedRow && 'bg-muted/40 dark:bg-muted/25'
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className={cn(
                'flex items-center justify-center w-6 h-6 rounded-lg',
                isMutedRow ? 'bg-muted text-muted-foreground' : config.badge
              )}
            >
              <CalendarDays className="h-3 w-3" />
            </div>
            <span className="text-sm font-bold text-foreground leading-tight whitespace-nowrap">{dateLabel}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-4 flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-sm sm:text-base text-foreground flex-shrink-0">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="font-bold">{week.filledSpots}/{week.capacity}</span>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0 min-w-[60px]">
              <div className="flex-1 h-1.5 bg-muted/50 rounded-full overflow-hidden min-w-0">
                <div
                  className={cn('h-full rounded-full transition-all duration-500', config.fillBar)}
                  style={{ width: `${Math.min(week.fillRate, 100)}%` }}
                />
              </div>
              <span className={cn('text-sm font-bold whitespace-nowrap flex-shrink-0', config.text)}>{Math.round(week.fillRate)}%</span>
            </div>
            <div className="text-xs font-medium whitespace-nowrap flex-shrink-0">
              {week.hasHardBlock ? (
                <span className="text-slate-500">Unavailable</span>
              ) : week.availableSpots > 0 ? (
                <span className={config.text}>{week.availableSpots} open</span>
              ) : (
                <span className="text-rose-600 dark:text-rose-400">Full</span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {week.hasHardBlock && (
                <div className="p-1 rounded-full bg-slate-200/80 dark:bg-slate-700/60">
                  <Ban className="h-3 w-3 text-slate-600 dark:text-slate-300" />
                </div>
              )}
              {!week.hasHardBlock && week.hasSoftBlock && (
                <div className="p-1 rounded-full bg-amber-200/80 dark:bg-amber-800/60">
                  <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-300" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={cn('p-4 space-y-4', isMutedRow && 'bg-muted/35 dark:bg-muted/20')}>
        {week.groups.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {week.groups.map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                embeddedInMutedWeek={isMutedRow}
                calendarMonthAnchor={calendarMonthAnchor}
              />
            ))}
          </div>
        ) : arTeamNames.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No groups scheduled</p>
        ) : null}
        {arTeamNames.length > 0 && (
          <div className="w-full">
            <ArTeamCard names={arTeamNames} tone={isMutedRow ? 'muted' : 'default'} />
          </div>
        )}
      </div>
    </div>
  )
}
